import { type FocusEvent, type MouseEvent, useCallback, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useMainSideNavStore } from '~/components/v2/store/features/workspace/useMainSideNavStore';

export type UseMainSideNavOverlayExpandParams = {
  hasOpenMainSideNavOverlay: boolean;
  isPinned: boolean;
};

export type UseMainSideNavOverlayExpandResult = {
  expandOnItemHover: (itemId?: string) => void;
  isOverlayExpanded: boolean;
  onBlur: (event: FocusEvent<HTMLElement>) => void;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  onFocus: (event: FocusEvent<HTMLElement>) => void;
  onMouseLeave: (event: MouseEvent<HTMLElement>) => void;
};

const MAIN_SIDE_NAV_SELECTOR = '[data-main-side-nav]';
const MAIN_SIDE_NAV_SELFCLOSE_SELECTOR = '[data-selfclose]';

/**
 * Survives a layout remount (workspace → settings). A self-close click collapses
 * the rail, then the new nav mounts under the cursor and `mouseenter` would
 * expand it again. Cleared when that remount is not under the pointer, or when
 * the pointer leaves a still-mounted rail.
 */
let suppressedHoverItemId: string | undefined;

/** Layout remounts keep this id; specs reset it with the other nav singletons. */
export const clearSuppressedMainSideNavHoverItem = () => {
  suppressedHoverItemId = undefined;
};

const blurActiveElementInMainSideNav = () => {
  const nav = document.querySelector(MAIN_SIDE_NAV_SELECTOR);
  const active = document.activeElement;

  if (nav instanceof HTMLElement && active instanceof HTMLElement && nav.contains(active)) {
    active.blur();
  }
};

/**
 * Collapsed-rail overlay: an item `mouseenter`, keyboard `:focus-visible`, or
 * an open overlay slot expands it. A `[data-selfclose]` pointer click
 * collapses under the cursor and ignores later `mouseenter` on that same item
 * (the settings layout remounts the rail under the pointer). Hovering a
 * different item, or leaving the rail and coming back, opens it again.
 */
export const useMainSideNavOverlayExpand = ({
  hasOpenMainSideNavOverlay,
  isPinned,
}: UseMainSideNavOverlayExpandParams): UseMainSideNavOverlayExpandResult => {
  const [isHoveringItem, setIsHoveringItem] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const wasOverlayOpenRef = useRef(hasOpenMainSideNavOverlay);
  const isOverlayExpanded = !isPinned && (isHoveringItem || isFocusWithin || hasOpenMainSideNavOverlay);

  const expandOnItemHover = useCallback((itemId?: string) => {
    if (itemId && itemId === suppressedHoverItemId) {
      return;
    }

    suppressedHoverItemId = undefined;
    setIsHoveringItem(true);
  }, []);

  useIsomorphicLayoutEffect(() => {
    const nav = document.querySelector(MAIN_SIDE_NAV_SELECTOR);

    // A label click shrinks the rail out from under the pointer and the settings
    // layout remounts it. The unmount leave is not a real exit (`isConnected` is
    // false), so drop the lock when the new rail is not under the pointer.
    // Returning to that item can expand. A remount still under the pointer keeps
    // the lock so the fresh `mouseenter` does not reopen it.
    if (nav instanceof HTMLElement && !nav.matches(':hover')) {
      suppressedHoverItemId = undefined;
    }
  }, []);

  const collapseFromAppContentDismiss = useCallback(() => {
    setIsHoveringItem(false);
    setIsFocusWithin(false);
    blurActiveElementInMainSideNav();
  }, []);

  useIsomorphicLayoutEffect(() => {
    const wasOpen = wasOverlayOpenRef.current;
    wasOverlayOpenRef.current = hasOpenMainSideNavOverlay;

    if (!wasOpen || hasOpenMainSideNavOverlay) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const nav = document.querySelector(MAIN_SIDE_NAV_SELECTOR);

      // Escape sets overlayClosedViaEscape so collapse keeps the restored trigger focused.
      if (useMainSideNavStore.getState().overlayClosedViaEscape) {
        useMainSideNavStore.getState().setOverlayClosedViaEscape(false);
        return;
      }

      if (nav instanceof HTMLElement && nav.matches(':hover')) {
        const active = document.activeElement;

        if (active instanceof HTMLElement && nav.contains(active)) {
          setIsFocusWithin(true);
        }

        return;
      }

      collapseFromAppContentDismiss();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [collapseFromAppContentDismiss, hasOpenMainSideNavOverlay]);

  const onFocus = useCallback((event: FocusEvent<HTMLElement>) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    // Mouse clicks on self-close links focus the item as a side effect and are not
    // :focus-visible. Expanding there would flash the rail open before the click
    // collapses it. Tab / keyboard focus still expands.
    if (event.target.matches(':focus-visible')) {
      setIsFocusWithin(true);
      return;
    }

    // Read the store, not render state. The credits popover refocuses its trigger
    // in the same turn that clears the slot; a stale "overlay open" flag would
    // expand the rail again after an outside click. Item hover is untouched.
    if (useMainSideNavStore.getState().activeOverlay !== null && event.target.closest('[data-main-side-nav-overlay]')) {
      setIsFocusWithin(true);
    }
  }, []);

  const onBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setIsFocusWithin(false);
  }, []);

  const onClick = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    // Only items marked data-selfclose collapse the overlay (nav links,
    // including New at Air). Credits, notifications, and help omit it so
    // dropdowns stay usable.
    const item = event.target.closest(MAIN_SIDE_NAV_SELFCLOSE_SELECTOR);

    if (!(item instanceof HTMLElement)) {
      return;
    }

    // Pointer clicks keep the cursor on the item. Blur so incidental focus
    // does not keep the overlay open. Keyboard activation (detail === 0)
    // must keep focus — blurring it would drop the tab order.
    if (event.detail !== 0) {
      suppressedHoverItemId = item.dataset.itemId;
      item.blur();
    }

    setIsHoveringItem(false);
    setIsFocusWithin(false);
  }, []);

  const onMouseLeave = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setIsHoveringItem(false);

      const pointerLeftMountedNav =
        event.currentTarget.isConnected &&
        (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget));

      if (pointerLeftMountedNav) {
        suppressedHoverItemId = undefined;
      }

      // Portaled overlay content is outside the nav; while a slot is open, leaving
      // the rail to reach that content must not drop focus-based expand.
      if (hasOpenMainSideNavOverlay) {
        return;
      }

      const active = document.activeElement;

      // Keyboard users keep the expanded rail until focus leaves the nav (onBlur).
      if (active instanceof HTMLElement && active.matches(':focus-visible')) {
        return;
      }

      setIsFocusWithin(false);
      blurActiveElementInMainSideNav();
    },
    [hasOpenMainSideNavOverlay],
  );

  return {
    expandOnItemHover,
    isOverlayExpanded,
    onBlur,
    onClick,
    onFocus,
    onMouseLeave,
  };
};
